import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import { readPendingAdminInviteToken } from '@/lib/pendingAdminInvite'

function mapAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) return 'Email or password is incorrect.'
  if (message.includes('Email not confirmed'))       return 'Please verify your email before logging in.'
  if (message.includes('Too many requests'))         return 'Too many attempts. Please wait a moment.'
  return 'Sign-in failed. Please try again.'
}

// A dedicated login page for drivetrack_admin, separate from the ordinary Login.tsx:
// admins never self-register (migration 028 blocks drivetrack_admin from signup
// metadata entirely — the only way in is accepting a superadmin's invite), so this page
// has no Google OAuth button and no "Register" link. It also actively rejects a
// successful sign-in for any non-admin account rather than silently routing them
// elsewhere, since someone landing here is specifically trying to reach the admin portal.
export default function AdminLogin() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile, refreshProfile, signOut } = useAuth()

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')

  useEffect(() => {
    if (profile?.primary_role === 'drivetrack_admin') {
      navigate('/app/admin', { replace: true })
    }
  }, [profile, navigate])

  if (profile?.primary_role === 'drivetrack_admin') return <LoadingScreen />

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError('')

    if (!email.trim())    return setError('Email is required.')
    if (!password.trim()) return setError('Password is required.')

    setLoading(true)
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
      if (signInError) {
        setError(mapAuthError(signInError.message))
        return
      }

      await refreshProfile()
      const { data: { user: freshUser } } = await supabase.auth.getUser()
      if (!freshUser) { setError('Session error. Please sign in again.'); return }

      const { data: profileData } = await supabase
        .from('profiles')
        .select('primary_role')
        .eq('id', freshUser.id)
        .single()

      if (profileData?.primary_role !== 'drivetrack_admin') {
        // A brand-new invitee (profile.primary_role still NULL, pre-acceptance) must be
        // allowed through to /accept-admin-invite, not rejected as "not an admin account"
        // — same mirrored check as below, before the hard rejection.
        const pendingAdminInviteToken = readPendingAdminInviteToken()
        if (profileData?.primary_role == null && pendingAdminInviteToken) {
          navigate('/accept-admin-invite', { replace: true })
          return
        }
        // Not an admin account — sign back out immediately rather than leaving an
        // authenticated-but-wrong-portal session sitting on this page.
        await signOut()
        setError("This isn't an admin account.")
        return
      }

      // An admin invitation token can only survive the create-account → confirm-email →
      // log-in round trip via storage (the confirmation email link lands on
      // /auth/callback, not back on this page, and is commonly opened in a brand new tab
      // — see pendingAdminInvite.ts) — mirrors Login.tsx's identical handling for a
      // pending instructor invite token.
      const pendingAdminInviteToken = readPendingAdminInviteToken()
      if (pendingAdminInviteToken) {
        navigate('/accept-admin-invite', { replace: true })
        return
      }

      // Only honour redirect if it's actually an admin-portal path — see Login.tsx's
      // identical guard and comment for why a stale ?redirect= from a different role's
      // session-expiry bounce must never be followed blindly.
      const redirectParam = searchParams.get('redirect')
      navigate(redirectParam?.startsWith('/app/admin') ? redirectParam : '/app/admin', { replace: true })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={60} onClick={() => navigate('/')} />
        </div>

        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px',
        }}>
          <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', textAlign: 'center' }}>
            Admin sign in
          </div>
          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 5, marginBottom: 18 }}>
            Platform administration only
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField
              label='Email'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='you@example.com'
              autoComplete='email'
              required
            />
            <FormField
              label='Password'
              type='password'
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder='Your password'
              autoComplete='current-password'
              required
            />

            {error && <div className='banner-error'>{error}</div>}

            <button type='submit' className='btn-primary' disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
